import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { BackendGate } from '../app/BackendGate'
import { apiClient, ApiError } from '../services/apiClient'
import { appRepository } from '../services/appRepository'
import { createId } from '../shared/utils/id'

const AppStoreContext = createContext(null)

function appReducer(state, action) {
  if (action.type === 'project/select') return { ...state, currentProjectId: action.projectId }
  if (action.type === 'entity/upsert') {
    const collection = state[action.collection]
    const index = collection.findIndex(item => item.id === action.entity.id)
    return {
      ...state,
      [action.collection]: index === -1
        ? [...collection, action.entity]
        : collection.map(item => item.id === action.entity.id ? action.entity : item),
    }
  }
  if (action.type === 'entity/update') {
    return {
      ...state,
      [action.collection]: state[action.collection].map(item => item.id === action.id ? { ...item, ...action.patch } : item),
    }
  }
  if (action.type === 'entity/remove') {
    return { ...state, [action.collection]: state[action.collection].filter(item => item.id !== action.id) }
  }
  if (action.type === 'entity/replace') return { ...state, [action.collection]: action.items }
  if (action.type === 'batch') return action.actions.reduce(appReducer, state)
  if (action.type === 'reset') return action.state
  return state
}

function getFriendlyError(error) {
  if (error instanceof ApiError) return error.message
  return '后端服务连接失败，请确认 API 和数据库已经启动'
}

export function AppStoreProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, undefined, () => {
    appRepository.backup()
    return appRepository.load()
  })
  const stateRef = useRef(state)
  const persistenceQueue = useRef(Promise.resolve())
  const [backend, setBackend] = useState({ status: 'booting', user: null, error: '' })
  const [dialog, setDialog] = useState(null)
  const [toasts, setToasts] = useState([])

  useEffect(() => { stateRef.current = state }, [state])
  useEffect(() => {
    if (backend.status === 'ready') appRepository.save(state)
  }, [backend.status, state])

  const notify = useCallback((message, tone = 'success') => {
    const id = createId('toast')
    setToasts(current => [...current, { id, message, tone }])
    window.setTimeout(() => setToasts(current => current.filter(item => item.id !== id)), 2600)
  }, [])

  const applyServerState = useCallback(payload => {
    const normalized = appRepository.normalize(payload)
    stateRef.current = normalized
    dispatch({ type: 'reset', state: normalized })
    return normalized
  }, [])

  const loadSession = useCallback(async ({ offerMigration = true } = {}) => {
    setBackend(current => ({ ...current, status: 'loading', error: '' }))
    try {
      await apiClient.health()
      const [{ user }, { projects }] = await Promise.all([apiClient.me(), apiClient.listProjects()])
      if (offerMigration && appRepository.needsBackendMigration()) {
        setBackend({ status: 'migration', user, error: '' })
        return
      }
      const preferredProjectId = projects.some(project => project.id === stateRef.current.currentProjectId)
        ? stateRef.current.currentProjectId
        : projects[0]?.id
      if (!preferredProjectId) throw new ApiError('当前账号还没有可访问的项目', 403, 'NO_PROJECT')
      const serverState = await apiClient.getProjectState(preferredProjectId)
      applyServerState(serverState)
      setBackend({ status: 'ready', user, error: '' })
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        apiClient.clearToken()
        setBackend({ status: 'login', user: null, error: '登录已失效，请重新登录' })
        return
      }
      setBackend(current => ({ ...current, status: 'error', error: getFriendlyError(error) }))
    }
  }, [applyServerState])

  useEffect(() => {
    if (apiClient.hasToken()) loadSession()
    else setBackend({ status: 'login', user: null, error: '' })
  }, [loadSession])

  const login = useCallback(async (email, password) => {
    setBackend({ status: 'loading', user: null, error: '' })
    try {
      await apiClient.login(email, password)
      await loadSession()
    } catch (error) {
      setBackend({ status: 'login', user: null, error: getFriendlyError(error) })
    }
  }, [loadSession])

  const migrate = useCallback(async () => {
    setBackend(current => ({ ...current, status: 'migrating', error: '' }))
    try {
      const result = await apiClient.importLocalState(stateRef.current)
      appRepository.markBackendMigration()
      if (result.state) applyServerState(result.state)
      setBackend(current => ({ ...current, status: 'ready', error: '' }))
      notify('浏览器数据已安全导入数据库')
    } catch (error) {
      setBackend(current => ({ ...current, status: 'migration', error: getFriendlyError(error) }))
    }
  }, [applyServerState, notify])

  const skipMigration = useCallback(async () => {
    appRepository.markBackendMigration()
    await loadSession({ offerMigration: false })
  }, [loadSession])

  const enqueue = useCallback(task => {
    const operation = persistenceQueue.current.then(task)
    persistenceQueue.current = operation.catch(() => undefined)
    operation.catch(error => notify(getFriendlyError(error), 'error'))
    return operation
  }, [notify])

  const persistAction = useCallback(async (action, beforeState, afterState) => {
    if (action.type === 'project/select' || action.type === 'reset' || action.type === 'batch') return
    const projectId = action.entity?.projectId || beforeState[action.collection]?.find(item => item.id === action.id)?.projectId || beforeState.currentProjectId
    if (action.collection === 'projects') {
      if (action.type === 'entity/remove') return
      const project = afterState.projects.find(item => item.id === (action.entity?.id || action.id))
      const existed = beforeState.projects.some(item => item.id === project?.id)
      if (project) await (existed ? apiClient.updateProject(project) : apiClient.createProject(project))
      return
    }
    if (action.collection === 'members') {
      if (action.type === 'entity/remove') await apiClient.deleteMember(action.id)
      else {
        const member = afterState.members.find(item => item.id === (action.entity?.id || action.id))
        if (member) await apiClient.saveMember(member)
      }
      return
    }
    if (action.type === 'entity/remove') await apiClient.deleteEntity(projectId, action.collection, action.id)
    else if (action.type === 'entity/replace') await apiClient.replaceEntities(projectId, action.collection, action.items)
    else {
      const entity = afterState[action.collection].find(item => item.id === (action.entity?.id || action.id))
      if (entity) await apiClient.saveEntity(projectId, action.collection, entity)
    }
  }, [])

  const runAction = useCallback(action => {
    const beforeState = stateRef.current
    const afterState = appReducer(beforeState, action)
    stateRef.current = afterState
    dispatch(action)
    return enqueue(() => persistAction(action, beforeState, afterState))
  }, [enqueue, persistAction])

  const selectProject = useCallback(async projectId => {
    if (!projectId || projectId === stateRef.current.currentProjectId) return
    const selected = appReducer(stateRef.current, { type: 'project/select', projectId })
    stateRef.current = selected
    dispatch({ type: 'project/select', projectId })
    setBackend(current => ({ ...current, status: 'switching', error: '' }))
    try {
      await persistenceQueue.current
      const serverState = await apiClient.getProjectState(projectId)
      applyServerState(serverState)
      setBackend(current => ({ ...current, status: 'ready', error: '' }))
    } catch (error) {
      setBackend(current => ({ ...current, status: 'error', error: getFriendlyError(error) }))
    }
  }, [applyServerState])

  const actions = useMemo(() => ({
    selectProject,
    upsert: (collection, entity) => runAction({ type: 'entity/upsert', collection, entity }),
    update: (collection, id, patch) => runAction({ type: 'entity/update', collection, id, patch }),
    remove: (collection, id) => runAction({ type: 'entity/remove', collection, id }),
    replace: (collection, items) => runAction({ type: 'entity/replace', collection, items }),
    batch: actionList => {
      const beforeState = stateRef.current
      const afterState = appReducer(beforeState, { type: 'batch', actions: actionList })
      stateRef.current = afterState
      dispatch({ type: 'batch', actions: actionList })
      return enqueue(async () => {
        let currentState = beforeState
        for (const action of actionList) {
          const nextState = appReducer(currentState, action)
          await persistAction(action, currentState, nextState)
          currentState = nextState
        }
      })
    },
    saveMember: (member, password = '') => {
      const safeMember = { ...member }
      const beforeState = stateRef.current
      const action = { type: 'entity/upsert', collection: 'members', entity: safeMember }
      const afterState = appReducer(beforeState, action)
      stateRef.current = afterState
      dispatch(action)
      return enqueue(() => apiClient.saveMember(password ? { ...safeMember, password } : safeMember))
    },
    uploadAssetFiles: (projectId, assetId, files) => enqueue(() => apiClient.uploadAssetFiles(projectId, assetId, files)),
    batchImportAssets: (projectId, input) => enqueue(async () => {
      const result = await apiClient.batchImportAssets(projectId, input)
      const importedActions = (result.assets || []).map(entity => ({ type: 'entity/upsert', collection: 'assets', entity }))
      if (importedActions.length) {
        const nextState = appReducer(stateRef.current, { type: 'batch', actions: importedActions })
        stateRef.current = nextState
        dispatch({ type: 'batch', actions: importedActions })
      }
      return result
    }),
    listAssetFiles: (projectId, assetId) => apiClient.listAssetFiles(projectId, assetId),
    getMediaBlob: fileId => apiClient.getMediaBlob(fileId),
    captureReference: (projectId, url, tags = []) => enqueue(async () => {
      const { item } = await apiClient.captureReference(projectId, url, tags)
      const action = { type: 'entity/upsert', collection: 'references', entity: item }
      const nextState = appReducer(stateRef.current, action)
      stateRef.current = nextState
      dispatch(action)
      return item
    }),
    openDialog: (type, payload = {}) => setDialog({ type, payload }),
    closeDialog: () => setDialog(null),
    notify,
    logout: () => {
      apiClient.clearToken()
      setBackend({ status: 'login', user: null, error: '' })
    },
    importBrowserBackup: async () => {
      try {
        const backup = appRepository.loadBackup()
        if (!backup) return notify('没有找到可导入的浏览器备份', 'error')
        const result = await apiClient.importLocalState(backup)
        if (result.state) applyServerState(result.state)
        notify('浏览器备份已导入数据库')
      } catch (error) {
        notify(getFriendlyError(error), 'error')
      }
    },
  }), [applyServerState, enqueue, notify, persistAction, runAction, selectProject])

  const value = useMemo(() => ({ state, dialog, toasts, backend, ...actions }), [state, dialog, toasts, backend, actions])
  if (backend.status !== 'ready') return <BackendGate backend={backend} localState={state} login={login} migrate={migrate} skipMigration={skipMigration} retry={() => loadSession()}/>
  return <AppStoreContext.Provider value={value}>{children}</AppStoreContext.Provider>
}

export function useAppStore() {
  const value = useContext(AppStoreContext)
  if (!value) throw new Error('useAppStore must be used inside AppStoreProvider')
  return value
}

export function useProjectData() {
  const store = useAppStore()
  const project = store.state.projects.find(item => item.id === store.state.currentProjectId)
  const filter = useCallback(collection => (store.state[collection] || []).filter(item => collection === 'members'
    ? (item.projectIds || [item.projectId]).includes(store.state.currentProjectId)
    : item.projectId === store.state.currentProjectId), [store.state])
  return { ...store, project, filter }
}
